import { CONFIG, PRESETS, ZONES, LINE_DEFS } from '../constants.js'
import { outerSize } from '../objects/courtGrid.js'
import { pickZonePalette } from '../tiles/zonePalette.js'
import { hoopOffsetRange } from '../hoops/hoopPosition.js'

// ============================================================================
//  controls — UI konfiguratora: sekcje (akordeon na desktopie ↔ zakładki na
//  mobile), kontrolki sceny (2D/3D, dzień/noc, blokada obrotu) i ekran
//  ładowania. Logika rozdzielona od sceny; komunikacja przez callbacki.
// ============================================================================

// Ikony kwadratów mobilnych — monochromatyczne SVG (currentColor: ciemne w dzień,
// białe w nocy). Pełny księżyc i „jednolita" kłódka = wypełnienie, nie kontur.
const ICONS = {
  sun: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="12" r="4.2" fill="currentColor" stroke="none"/><path d="M12 2.5v2.6M12 18.9v2.6M4.6 4.6l1.8 1.8M17.6 17.6l1.8 1.8M2.5 12h2.6M18.9 12h2.6M4.6 19.4l1.8-1.8M17.6 6.4l1.8-1.8"/></svg>',
  moon: '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path fill="currentColor" d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z"/></svg>',
  rotate: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 11.5A8 8 0 0 0 6.3 6.3L3.5 9"/><path d="M3.5 4v5h5"/><path d="M4 12.5A8 8 0 0 0 17.7 17.7l2.8-2.7"/><path d="M20.5 20v-5h-5"/></svg>',
  lock: '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="M7 10V7a5 5 0 0 1 10 0v3" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><rect x="4.5" y="10" width="15" height="10.5" rx="2.2" fill="currentColor"/></svg>',
}

// Liczba kolumn swatchy dla równych wierszy (ostatni co najwyżej o 1 krótszy):
// najmniej wierszy mieszczących się w `max`, potem równy podział. 10→5+5, 14→7+7, 11→6+5.
function balancedCols(n, max = 7) {
  const rows = Math.ceil(n / max) || 1
  return Math.ceil(n / rows)
}

// Kontroler kontrolek sceny: wspólny stan {view, mode, locked} steruje stosem
// trzech kwadratów on/off (#scene-controls) w prawym górnym rogu sceny — te same
// na desktopie i mobile. Każdy kwadrat pokazuje BIEŻĄCY stan ikoną; klik przełącza
// stan, przerysowuje i woła odpowiedni callback. Render początkowy odbija stan
// bez wołania callbacków (scena startuje w 3D / dzień / odblokowana).
export function initSceneControls({ state, onViewChange, onToggle, onLock }) {
  const box = document.getElementById('scene-controls')
  if (!box) return
  const s = { view: state.view || '3d', mode: 'day', locked: false }

  box.innerHTML = `
    <button class="scene-ctrl__btn" data-ctrl="view" type="button"></button>
    <button class="scene-ctrl__btn" data-ctrl="mode" type="button"></button>
    <button class="scene-ctrl__btn" data-ctrl="lock" type="button"></button>`
  const bView = box.querySelector('[data-ctrl="view"]')
  const bMode = box.querySelector('[data-ctrl="mode"]')
  const bLock = box.querySelector('[data-ctrl="lock"]')

  function render() {
    bView.textContent = s.view.toUpperCase()
    bView.setAttribute('aria-label', s.view === '3d' ? 'Widok 3D — przełącz na 2D' : 'Widok 2D — przełącz na 3D')
    bMode.innerHTML = s.mode === 'night' ? ICONS.moon : ICONS.sun
    bMode.setAttribute('aria-label', s.mode === 'night' ? 'Noc — przełącz na dzień' : 'Dzień — przełącz na noc')
    bLock.innerHTML = s.locked ? ICONS.lock : ICONS.rotate
    bLock.setAttribute('aria-pressed', String(s.locked))
    bLock.setAttribute('aria-label', s.locked ? 'Obrót zablokowany — odblokuj' : 'Scena się obraca — zablokuj')
  }

  // Settery: zmiana stanu → render → callback (efekt na scenie).
  function setView(v) { if (v === s.view) return; s.view = v; state.view = v; render(); onViewChange?.(v) }
  function setMode(m) { if (m === s.mode) return; s.mode = m; document.body.classList.toggle('is-night', m === 'night'); render(); onToggle?.(m) }
  function setLock(l) { if (l === s.locked) return; s.locked = l; render(); onLock?.(l) }

  bView.addEventListener('click', () => setView(s.view === '3d' ? '2d' : '3d'))
  bMode.addEventListener('click', () => setMode(s.mode === 'day' ? 'night' : 'day'))
  bLock.addEventListener('click', () => setLock(!s.locked))

  render()
}

// Sekcje sidebara. Jeden stan „aktywna sekcja" steruje akordeonem (desktop)
// i zakładkami (mobile) — to ten sam mechanizm: dokładnie jedna otwarta.
// Kontrolki sceny (2D/3D, dzień/noc, kłódka) są poza sidebarem — pływają nad
// sceną (desktop) lub jako stos kwadratów w prawym górnym rogu (mobile).
const SECTIONS = [
  { key: 'model', label: 'Model płytki', tab: 'Model' },
  { key: 'size', label: 'Wymiary boiska', tab: 'Wymiary' },
  { key: 'colors', label: 'Kolorystyka', tab: 'Kolory' },
  { key: 'lines', label: 'Linie boiska', tab: 'Linie' },
  { key: 'hoop', label: 'Kosz', tab: 'Kosz' },
  { key: 'extras', label: 'Dodatki', tab: 'Dodatki' },
  { key: 'saved', label: 'Zapisane boiska', tab: 'Zapisane' },
]

// Buduje pełny konfigurator: sekcje, model płytki, wymiary (steppery),
// kolory stref, wybór kosza (karty) i zakładki mobile. Kontrolki sceny
// (2D/3D, dzień/noc, kłódka) buduje osobno initSceneControls.
// Dane z katalogów (tiles.json, hoops.json — hoopCatalog może być null,
// gdy hoops.json się nie wczytał). Callbacki:
//   onModelChange(modelId), onSizeChange({widthTiles,lengthTiles,runoffTiles}),
//   onColorChange(zoneKey, colorId), onHoopChange(hoopId).
export function initConfigurator({ catalog, hoopCatalog, state, onModelChange, onSizeChange, onRunoffChange, onPresetLengthChange, onColorChange, onHoopChange, onHoopOffsetChange, onPreset, onLineToggle, savedCourts, onLoadSaved, onSavedChange, initialSection }) {
  const root = document.getElementById('cfg-panel')
  const tabsBox = document.getElementById('cfg-tabs')
  if (!root || !tabsBox) return

  // --- Szkielet: sekcje + zakładki ---
  root.innerHTML = SECTIONS.map((s) => `
    <section class="sec" data-sec="${s.key}">
      <button class="sec__head" type="button">
        <span>${s.label}</span><span class="sec__chev">▾</span>
      </button>
      <div class="sec__body"></div>
    </section>
  `).join('')
  tabsBox.innerHTML = SECTIONS.map(
    (s) => `<button class="cfg-tabs__btn" type="button" data-sec="${s.key}">${s.tab}</button>`
  ).join('')

  const body = (key) => root.querySelector(`.sec[data-sec="${key}"] .sec__body`)

  // --- Dokładnie jedna sekcja aktywna (akordeon = zakładki) ---
  let active = initialSection || 'model'
  function setActive(key) {
    active = key
    root.querySelectorAll('.sec').forEach((el) => {
      el.classList.toggle('is-active', el.dataset.sec === key)
    })
    tabsBox.querySelectorAll('.cfg-tabs__btn').forEach((b) => {
      b.classList.toggle('is-active', b.dataset.sec === key)
    })
  }
  root.querySelectorAll('.sec__head').forEach((h) => {
    h.addEventListener('click', () => setActive(h.closest('.sec').dataset.sec))
  })
  tabsBox.querySelectorAll('.cfg-tabs__btn').forEach((b) => {
    b.addEventListener('click', () => setActive(b.dataset.sec))
  })
  setActive(active)

  const STEP = {
    widthTiles: { ...CONFIG.WIDTH, label: 'Szerokość' },
    lengthTiles: { ...CONFIG.LENGTH, label: 'Długość' },
    runoffTiles: { ...CONFIG.RUNOFF, label: 'Wybieg' },
  }
  // Sekcja Kolory: strefy nawierzchni; strefy wskazane przez colorKey w LINE_DEFS
  // (linesThree/linesJr) sterują swatche pod wierszami linii 3 pkt w sekcji „Linie".
  const lineColorKeys = new Set(LINE_DEFS.map((d) => d.colorKey).filter(Boolean))
  const colorZones = ZONES.filter((z) => !lineColorKeys.has(z.key))

  const modelDef = () => catalog.models.find((m) => m.id === state.model) || catalog.models[0]
  const tileMm = () => modelDef().size.width
  const edgeMm = () => modelDef().edge ?? 80 // ten sam fallback co przy budowie płyty (main)
  const meters = (tiles) => ((tiles * tileMm()) / 1000).toFixed(2)

  // Prefiks ścieżek publicznych (Vite). Wspólny dla miniatur modeli i koszy.
  const base = import.meta.env.BASE_URL || '/'
  // Paleta (zakres barw) per STREFA: strefy płytek → paleta bieżącego modelu,
  // strefy linii → wspólna paleta 'court-lines' — źródło swatchy w sekcjach kolorów.
  const zonePalette = (zoneKey) => pickZonePalette(catalog, state.model, zoneKey)

  // --- Model płytki: karty (zdjęcie + nazwa), wzorzec 1:1 z sekcją „Kosz" ---
  const modelBox = document.createElement('div')
  modelBox.className = 'cfg__models'
  catalog.models.forEach((m) => {
    const card = document.createElement('button')
    card.type = 'button'
    card.className = 'tile-card' + (state.model === m.id ? ' is-active' : '')
    card.dataset.model = m.id
    // Puste alt — grafika dekoracyjna, nazwa stoi pod spodem. Brak miniatury →
    // karta pokazuje samą nazwę (degradacja jak przy koszach).
    const img = document.createElement('img')
    img.className = 'tile-card__img'
    img.src = base + m.thumb
    img.alt = ''
    img.addEventListener('error', () => { img.style.display = 'none' })
    const name = document.createElement('span')
    name.className = 'tile-card__name'
    name.textContent = m.name
    card.appendChild(img)
    card.appendChild(name)
    card.addEventListener('click', async () => {
      if (state.model === m.id) return
      modelBox.querySelectorAll('.tile-card').forEach((c) => c.classList.remove('is-active'))
      card.classList.add('is-active')
      await onModelChange(m.id)
      syncStepperValues()
      setActivePreset(state.preset)
      syncStepperLocks()
      refreshMeters()
      // Zakres barw zmienił się wraz z modelem — przerysuj swatche stref i linii 3 pkt.
      renderColors()
      renderLineSwatches()
    })
    modelBox.appendChild(card)
  })
  body('model').appendChild(modelBox)

  // --- Presety wymiarów (chipy nad stepperami) ---
  const presetBox = document.createElement('div')
  presetBox.className = 'cfg__presets'
  const setActivePreset = (id) => {
    presetBox.querySelectorAll('.cfg__preset').forEach((c) => {
      c.classList.toggle('is-active', c.dataset.preset === id)
    })
  }
  PRESETS.forEach((p) => {
    const chip = document.createElement('button')
    chip.type = 'button'
    chip.className = 'cfg__preset' + (state.preset === p.id ? ' is-active' : '')
    chip.dataset.preset = p.id
    chip.textContent = p.label
    chip.addEventListener('click', async () => {
      await onPreset(p.id)
      setActivePreset(p.id)
      syncStepperValues()
      syncStepperLocks()
      refreshMeters()
    })
    presetBox.appendChild(chip)
  })
  body('size').appendChild(presetBox)

  // --- Wymiary boiska (steppery) ---
  const stepBox = document.createElement('div')
  stepBox.className = 'cfg__steppers'
  const valueEls = {}
  const meterEls = {}
  Object.entries(STEP).forEach(([key, cfg]) => {
    const row = document.createElement('div')
    row.className = 'cfg__stepper'
    row.innerHTML = `
      <div class="cfg__stepper-head">
        <span class="cfg__stepper-label">${cfg.label}</span>
        <span class="cfg__stepper-meters"></span>
      </div>
      <div class="cfg__stepper-ctrl">
        <button class="cfg__btn" data-act="dec" aria-label="mniej">−</button>
        <span class="cfg__stepper-val">${state[key]}</span>
        <button class="cfg__btn" data-act="inc" aria-label="więcej">+</button>
      </div>
    `
    valueEls[key] = row.querySelector('.cfg__stepper-val')
    meterEls[key] = row.querySelector('.cfg__stepper-meters')
    row.querySelector('[data-act="dec"]').addEventListener('click', () => step(key, -1, cfg))
    row.querySelector('[data-act="inc"]').addEventListener('click', () => step(key, +1, cfg))
    stepBox.appendChild(row)
  })
  body('size').appendChild(stepBox)

  async function step(key, delta, cfg) {
    const p = PRESETS.find((x) => x.id === state.preset)
    if (p?.lock?.includes(key)) return // preset blokuje tę oś (guard — przyciski i tak disabled)
    // W presecie z regulowaną długością (5×5) sufit stepperów = maxCourtLenM
    // przeliczone na płytki (pole gry ≤ 28 m).
    let max = cfg.max
    if (key === 'lengthTiles' && p?.maxCourtLenM) {
      max = Math.min(max, Math.floor((p.maxCourtLenM * 1000) / tileMm()))
    }
    // Wybieg ma jedno, globalne minimum (CONFIG.RUNOFF.min = 0) — zejście do
    // boiska bez wybiegu wolno w każdym presecie i w trybie ręcznym.
    const next = Math.min(max, Math.max(cfg.min, state[key] + delta))
    if (next === state[key]) return
    state[key] = next
    valueEls[key].textContent = next
    if (key === 'runoffTiles') {
      // Wybieg jest niezależny od presetu: zmiana nie wyrzuca z FIBA i nie gubi
      // linii (layouty „+1pc" — wybieg 1 płytka przy zachowanym standardzie).
      await onRunoffChange(next)
    } else if (key === 'lengthTiles' && p?.maxCourtLenM) {
      // Długość regulowana WEWNĄTRZ presetu 5×5 — chip i linie FIBA zostają.
      await onPresetLengthChange(next)
    } else {
      // onSizeChange (main) ustawia lineW/lineL/spec='compact'/preset='custom'.
      await onSizeChange({
        widthTiles: state.widthTiles,
        lengthTiles: state.lengthTiles,
        runoffTiles: state.runoffTiles,
      })
      setActivePreset(state.preset) // 'custom' ⇒ żaden chip niezaznaczony
      syncStepperValues() // tryb ręczny mógł podbić wybieg 0 → 1 (min. poza „Małym")
      syncStepperLocks()
    }
    refreshMeters()
  }

  // Preset może blokować wymiary pola gry (regulamin FIBA: 3×3 obie osie,
  // 5×5 szerokość): wyszarz steppery zablokowanych osi; wybieg zawsze aktywny.
  function syncStepperLocks() {
    const p = PRESETS.find((x) => x.id === state.preset)
    const locked = new Set(p?.lock || [])
    Object.keys(STEP).forEach((key) => {
      const row = valueEls[key].closest('.cfg__stepper')
      const off = locked.has(key)
      row.classList.toggle('is-disabled', off)
      row.querySelectorAll('.cfg__btn').forEach((b) => { b.disabled = off })
    })
  }

  // Synchronizuje wyświetlane liczby płytek ze stanem (po presecie/zmianie modelu,
  // gdy ceil mógł zmienić liczbę płytek bez kliknięcia w stepper).
  function syncStepperValues() {
    Object.keys(STEP).forEach((key) => { valueEls[key].textContent = state[key] })
  }

  // Odczyt pod stepperem: pole gry w płytkach + wymiar ZEWNĘTRZNY boiska w
  // metrach — ZAWSZE z obrzeżem (i wybiegiem, gdy jest), bo to realny gabaryt
  // produktu (uwaga klienta 04.09.2026: wymiary podawać z obrzeżem; wcześniej
  // stały tu same metry pola gry). Wybieg: szerokość pasa w płytkach i metrach.
  function refreshMeters() {
    const outer = outerSize({
      widthTiles: state.widthTiles, lengthTiles: state.lengthTiles, runoffTiles: state.runoffTiles,
      tileMm: tileMm(), edgeMm: edgeMm(),
    })
    const suffix = state.runoffTiles > 0 ? 'z wybiegiem i obrzeżem' : 'z obrzeżem'
    meterEls.widthTiles.textContent = `${state.widthTiles} płytek · ${outer.widthM.toFixed(2)} m ${suffix}`
    meterEls.lengthTiles.textContent = `${state.lengthTiles} płytek · ${outer.lengthM.toFixed(2)} m ${suffix}`
    meterEls.runoffTiles.textContent = `${state.runoffTiles} płytek · ${meters(state.runoffTiles)} m`
  }
  refreshMeters()
  syncStepperLocks() // stan początkowy (np. start z zapisu na presecie FIBA)

  // --- Kolorystyka stref — przerysowywana przy zmianie modelu (paleta per model) ---
  const colorBox = document.createElement('div')
  colorBox.className = 'cfg__colors'
  body('colors').appendChild(colorBox)

  function renderColors() {
    colorBox.innerHTML = ''
    colorZones.forEach((zone) => {
      const palette = zonePalette(zone.key)
      const row = document.createElement('div')
      row.className = 'cfg__color-row'
      const label = document.createElement('span')
      label.className = 'cfg__color-label'
      label.textContent = zone.label
      const swBox = document.createElement('div')
      swBox.className = 'cfg__swatches'
      // Równy rozkład kółek na wiersze (siatka) — patrz balancedCols + reguła CSS.
      swBox.style.setProperty('--sw-cols', balancedCols(palette.length))
      palette.forEach((p) => {
        const b = document.createElement('button')
        b.className = 'swatch' + (state.colors[zone.key] === p.id ? ' is-active' : '')
        b.style.background = p.swatch
        b.title = p.name
        b.setAttribute('aria-label', `${zone.label}: ${p.name}`)
        b.addEventListener('click', () => {
          state.colors[zone.key] = p.id
          swBox.querySelectorAll('.swatch').forEach((s) => s.classList.remove('is-active'))
          b.classList.add('is-active')
          onColorChange(zone.key, p.id)
        })
        swBox.appendChild(b)
      })
      row.appendChild(label)
      row.appendChild(swBox)
      colorBox.appendChild(row)
    })
  }
  renderColors()

  // --- Linie boiska (przełączniki widoczności + swatche kolorów linii 3 pkt) ---
  // Definicje ze wspólnego źródła (constants) — jedno źródło etykiet z payloadem wyceny.
  const LINE_TOGGLES = LINE_DEFS
  const linesBox = document.createElement('div')
  linesBox.className = 'cfg__lines'
  const ticksRowRef = {}
  // Referencje do swatchy przy liniach z colorKey (3 pkt senior/junior) —
  // wypełniane w pętli, przerysowywane przy starcie i po zmianie modelu.
  // Swatche widoczne tylko przy zaznaczonym checkboxie danej linii.
  const lineSwatchRefs = []
  function renderLineSwatches() {
    lineSwatchRefs.forEach(({ swBox, colorKey, label, lineKey }) => {
      const palette = zonePalette(colorKey)
      swBox.innerHTML = ''
      swBox.hidden = !state.lines[lineKey]
      palette.forEach((p) => {
        const b = document.createElement('button')
        b.type = 'button'
        b.className = 'swatch' + (state.colors[colorKey] === p.id ? ' is-active' : '')
        b.style.background = p.swatch
        b.title = p.name
        b.setAttribute('aria-label', `${label}: ${p.name}`)
        b.addEventListener('click', (e) => {
          // Wiersz jest <label> — bez preventDefault klik w swatch przełączałby checkbox.
          e.preventDefault()
          state.colors[colorKey] = p.id
          swBox.querySelectorAll('.swatch').forEach((s) => s.classList.remove('is-active'))
          b.classList.add('is-active')
          onColorChange(colorKey, p.id)
        })
        swBox.appendChild(b)
      })
    })
  }
  // Ostrzeżenie „niewymiarowa" przy liniach za 3 pkt: promień łuku dociśnięty
  // głębokością boiska to linia umowna — klient musi to widzieć (uwaga
  // klientki: nie każdy zauważy, że regulaminowy promień się nie mieści).
  // Zamiast tekstowego badge'a: czerwona ikonka z wykrzyknikiem, klik otwiera
  // dymek z wyjaśnieniem (uwaga 25.08.2026). Stan liczy Court (state.lineFit).
  const badgeEls = {}
  const checkEls = {}
  LINE_TOGGLES.forEach((t) => {
    const row = document.createElement('label')
    row.className = 'cfg__line-row'
    const cb = document.createElement('input')
    cb.type = 'checkbox'
    cb.className = 'cfg__line-check'
    cb.checked = !!state.lines[t.key]
    const label = document.createElement('span')
    label.className = 'cfg__line-label'
    label.textContent = t.label
    row.appendChild(cb)
    row.appendChild(label)

    if (t.key === 'three' || t.key === 'threeJr') {
      const warn = document.createElement('button')
      warn.type = 'button'
      warn.className = 'cfg__line-warn'
      warn.textContent = '!'
      warn.title = 'Linia niewymiarowa — kliknij po wyjaśnienie'
      warn.setAttribute('aria-label', `${t.label}: linia niewymiarowa — pokaż wyjaśnienie`)
      warn.setAttribute('aria-expanded', 'false')
      const radiusTxt = t.key === 'three' ? '6,75 m' : '6,00 m'
      const tip = document.createElement('span')
      tip.className = 'cfg__line-warn-tip'
      tip.hidden = true
      tip.textContent = `Linia niewymiarowa: łuk za 3 pkt jest przycięty głębokością boiska i biegnie bliżej kosza niż regulaminowe ${radiusTxt}. Linia umowna, nie regulaminowa — powiększ pole gry w głąb, by zmieścić pełny łuk.`
      const setOpen = (open) => {
        tip.hidden = !open
        warn.setAttribute('aria-expanded', String(open))
      }
      // preventDefault: wiersz jest <label> — bez tego klik w ikonę/dymek
      // przełączałby checkbox widoczności linii.
      warn.addEventListener('click', (e) => {
        e.preventDefault()
        e.stopPropagation()
        setOpen(tip.hidden)
      })
      tip.addEventListener('click', (e) => e.preventDefault())
      document.addEventListener('click', (e) => {
        if (!warn.contains(e.target) && !tip.contains(e.target)) setOpen(false)
      })
      badgeEls[t.key] = { warn, setOpen }
      row.appendChild(warn)
      row.appendChild(tip)
    }

    // Swatche koloru pod wierszem linii z colorKey (3 pkt senior/junior) —
    // pusty kontener; wypełnia go renderLineSwatches(). Widoczny tylko przy
    // zaznaczonym checkboxie (steruje nim też syncLines po zmianach ze stanu).
    let swBox = null
    if (t.colorKey) {
      swBox = document.createElement('div')
      swBox.className = 'cfg__swatches cfg__line-swatches'
      swBox.hidden = !state.lines[t.key]
      lineSwatchRefs.push({ swBox, colorKey: t.colorKey, label: t.label, lineKey: t.key })
      row.appendChild(swBox)
    }

    cb.addEventListener('change', () => {
      onLineToggle(t.key, cb.checked)
      if (t.key === 'key') syncTicksEnabled()
      if (swBox) swBox.hidden = !cb.checked
    })

    checkEls[t.key] = cb
    if (t.key === 'ticks') ticksRowRef.cb = cb
    linesBox.appendChild(row)
  })
  // Ticki bez pola nie mają sensu — wyszarz, gdy „Pole" wyłączone (geometria je i tak pomija).
  function syncTicksEnabled() {
    if (!ticksRowRef.cb) return
    const on = !!state.lines.key
    ticksRowRef.cb.disabled = !on
    ticksRowRef.cb.closest('.cfg__line-row').classList.toggle('is-disabled', !on)
  }
  syncTicksEnabled()
  renderLineSwatches()
  // Dosunięcie sekcji „Linie" do stanu po zmianach spoza niej (preset „Mały"
  // wyłącza linię juniorską w main) — checkboxy i widoczność swatchy ze stanu.
  function syncLines() {
    Object.entries(checkEls).forEach(([key, cb]) => { cb.checked = !!state.lines[key] })
    lineSwatchRefs.forEach(({ swBox, lineKey }) => { swBox.hidden = !state.lines[lineKey] })
    syncTicksEnabled()
  }
  // Pokaż/schowaj ikony ostrzeżenia wg dopasowania policzonego przy budowie
  // linii; chowając ikonę, zamknij też jej dymek.
  function updateLineBadges(fit = state.lineFit) {
    if (!fit) return
    Object.entries(badgeEls).forEach(([key, { warn, setOpen }]) => {
      const show = fit[key] === false
      warn.style.display = show ? '' : 'none'
      if (!show) setOpen(false)
    })
  }
  updateLineBadges()
  body('lines').appendChild(linesBox)

  // --- Kosz: karty z miniaturką (zdjęcie + nazwa) + suwak pozycji kosza ---
  let posSlider = null
  let posReadout = null
  if (hoopCatalog && hoopCatalog.hoops.length) {
    const hoopBox = document.createElement('div')
    hoopBox.className = 'cfg__hoops'
    hoopCatalog.hoops.forEach((h) => {
      const card = document.createElement('button')
      card.type = 'button'
      card.className = 'hoop-card' + (state.hoop === h.id ? ' is-active' : '')
      // Puste alt — grafika dekoracyjna, nazwa stoi obok (wzorzec logo brandingu).
      // createElement/textContent zamiast innerHTML — dane katalogu nie są
      // interpolowane do markupu (idiom jak przy nazwach modeli płytek).
      const img = document.createElement('img')
      img.className = 'hoop-card__img'
      img.src = base + h.thumb
      img.alt = ''
      // Brak miniaturki → karta pokazuje samą nazwę.
      img.addEventListener('error', () => { img.style.display = 'none' })
      const name = document.createElement('span')
      name.className = 'hoop-card__name'
      name.textContent = h.name
      card.appendChild(img)
      card.appendChild(name)
      card.addEventListener('click', () => {
        state.hoop = h.id
        hoopBox.querySelectorAll('.hoop-card').forEach((c) => c.classList.remove('is-active'))
        card.classList.add('is-active')
        onHoopChange(h.id)
      })
      hoopBox.appendChild(card)
    })
    body('hoop').appendChild(hoopBox)

    // --- Pozycja kosza: suwak, krok = 1 płytka (wzorzec: oncourt.online).
    // Lewo = w głąb boiska (kosz na nawierzchni, linie i trumna idą za nim),
    // prawo = za linię końcową (trumna się skraca); 0 = pozycja standardowa.
    // Zakres zależy od boiska i kosza — przelicza updateHoopSlider (wołany też
    // z main po każdej przebudowie nawierzchni).
    const posBox = document.createElement('div')
    posBox.className = 'cfg__hoop-pos'
    posBox.innerHTML = `
      <div class="cfg__stepper-head">
        <span class="cfg__stepper-label">Pozycja kosza</span>
        <span class="cfg__stepper-meters" data-role="hoop-pos-readout"></span>
      </div>
      <input type="range" class="cfg__hoop-slider" step="1" aria-label="Pozycja kosza (krok = 1 płytka)">
      <div class="cfg__hoop-scale"><span>na boisku</span><span>za linią</span></div>`
    posSlider = posBox.querySelector('.cfg__hoop-slider')
    posReadout = posBox.querySelector('[data-role="hoop-pos-readout"]')
    posSlider.addEventListener('input', () => onHoopOffsetChange(Number(posSlider.value)))
    body('hoop').appendChild(posBox)
  } else {
    body('hoop').innerHTML = '<p class="cfg__placeholder">Niedostępne</p>'
  }

  // Zakres i odczyt suwaka pozycji kosza wg bieżącego boiska/kosza. Wołane przy
  // budowie i po każdej przebudowie nawierzchni (offset mógł zostać przycięty).
  function updateHoopSlider() {
    if (!posSlider) return
    const h = hoopCatalog.hoops.find((x) => x.id === state.hoop) || hoopCatalog.hoops[0]
    const range = hoopOffsetRange({
      lengthTiles: state.lengthTiles,
      tileM: tileMm() / 1000,
      rimFromBaseline: h.rimFromBaseline ?? 1.462,
    })
    posSlider.min = range.min
    posSlider.max = range.max
    posSlider.value = state.hoopOffsetTiles
    const k = state.hoopOffsetTiles
    posReadout.textContent =
      k === 0 ? 'standard · za linią końcową'
        : k > 0 ? `${k} ${k === 1 ? 'płytka' : 'płytki'} w głąb boiska`
          : `${-k} ${-k === 1 ? 'płytka' : 'płytki'} za linią`
  }
  updateHoopSlider()

  // --- Dodatki (na razie puste) ---
  body('extras').innerHTML = '<p class="cfg__placeholder">Wkrótce</p>'
  // Uwagi/życzenia klienta zbieramy dopiero w modalu wyceny (pole „Uwagi").

  // --- Zapisane boiska: lista zapisów z localStorage. Sekcja i zakładka są
  // ukrywane, gdy nie ma zapisów (pojawiają się po pierwszym „Zapisz"). ---
  const savedSec = root.querySelector('.sec[data-sec="saved"]')
  const savedTab = tabsBox.querySelector('.cfg-tabs__btn[data-sec="saved"]')
  const fmtDate = (iso) => {
    const d = new Date(iso)
    return `${d.toLocaleDateString('pl-PL')}, ${d.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })}`
  }
  function renderSaved() {
    const items = savedCourts ? savedCourts.list() : []
    const empty = items.length === 0
    savedSec.style.display = empty ? 'none' : ''
    savedTab.style.display = empty ? 'none' : ''
    if (empty) {
      if (active === 'saved') setActive('model')
      return
    }
    body('saved').innerHTML = items.map((e) => {
      const m = catalog.models.find((x) => x.id === e.cfg.model) || catalog.models[0]
      // Tytuł zapisu: wymiar ZEWNĘTRZNY boiska (z wybiegiem i obrzeżem) — jak
      // wszędzie indziej w konfiguratorze (uwaga klienta 04.09.2026).
      const o = outerSize({
        widthTiles: e.cfg.widthTiles, lengthTiles: e.cfg.lengthTiles, runoffTiles: e.cfg.runoffTiles,
        tileMm: m.size.width, edgeMm: m.edge ?? 80,
      })
      const w = o.widthM.toFixed(2)
      const l = o.lengthM.toFixed(2)
      return `
        <div class="saved-row" data-id="${e.id}" role="button" tabindex="0">
          <img class="saved-row__thumb" src="${base + m.thumb}" alt="">
          <span class="saved-row__info">
            <span class="saved-row__title">${m.name} · ${w} × ${l} m z obrzeżem</span>
            <span class="saved-row__date">${fmtDate(e.savedAt)}</span>
          </span>
          <button class="saved-row__del" type="button" aria-label="Usuń zapis">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 6h18M8 6V4h8v2m1 0-1 14H8L7 6"/></svg>
          </button>
        </div>`
    }).join('')
    body('saved').querySelectorAll('.saved-row').forEach((row) => {
      const id = row.dataset.id
      const img = row.querySelector('.saved-row__thumb')
      img.addEventListener('error', () => { img.style.display = 'none' })
      row.querySelector('.saved-row__del').addEventListener('click', (ev) => {
        ev.stopPropagation()
        savedCourts.remove(id)
        renderSaved()
        onSavedChange?.()
      })
      // Wczytanie zapisu — ten sam handler dla myszy i klawiatury (wiersz jest role="button").
      const load = () => {
        const entry = savedCourts.list().find((x) => x.id === id)
        if (entry) onLoadSaved?.(entry.cfg)
      }
      row.addEventListener('click', load)
      row.addEventListener('keydown', (ev) => {
        // Enter/Spacja na koszu obsługuje natywnie sam <button> — nie przechwytujemy zdarzenia z bąbelkowania.
        if (ev.target !== row) return
        if (ev.key !== 'Enter' && ev.key !== ' ') return
        ev.preventDefault() // Spacja domyślnie przewija stronę
        load()
      })
    })
  }
  renderSaved()

  // refreshColors/syncLines: main dosuwa sidebar po zmianach stanu spoza sekcji
  // (automatyczny kolor trumny przy zmianie rozmiaru, „Mały" wyłącza junior).
  return { renderSaved, setActive, updateLineBadges, updateHoopSlider, refreshColors: renderColors, syncLines }
}

export function hideLoader() {
  const loader = document.getElementById('loader')
  if (loader) loader.classList.add('is-hidden')
}
